import { useTelegram } from '@/shared/lib/telegram'
import { useTheme } from '@/shared/hooks/use-theme'
import { useNavigate } from '@tanstack/react-router'
import { Moon, Sun, Smartphone, FileText, Shield, ChevronRight, Bell, Megaphone } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useEventNotificationPreferences, useUpdateEventNotificationPreferences } from '@/entities/event'
import { useUserSettings, useUpdateUserSettings } from '@/entities/user'
import type { Theme } from '@/shared/lib/theme'

/**
 * Настройки профиля
 */
export function ProfileSettings() {
  const { hapticFeedback, initData } = useTelegram()
  const { theme, effectiveTheme, setTheme, isPlatform } = useTheme()
  const navigate = useNavigate()

  const { data: preferences, isLoading: isPrefsLoading } = useEventNotificationPreferences()
  const updatePrefsMutation = useUpdateEventNotificationPreferences()
  const { data: userSettings, isLoading: isUserSettingsLoading } = useUserSettings()
  const updateUserSettingsMutation = useUpdateUserSettings()

  const handleTheme = (newTheme: Theme) => {
    hapticFeedback.impactOccurred('light')
    setTheme(newTheme)
  }

  const handlePrivacyClick = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/privacy' })
  }

  const handleOfferClick = () => {
    hapticFeedback.impactOccurred('light')
    navigate({ to: '/offer' })
  }

  const toggleNotifications = () => {
    if (isPrefsLoading || updatePrefsMutation.isPending || !preferences) return
    hapticFeedback.impactOccurred('light')
    updatePrefsMutation.mutate(!preferences.eventReminders)
  }

  const isNotificationsEnabled = preferences?.eventReminders !== false
  const isBroadcastsEnabled = userSettings
    ? userSettings.newProducts !== false || userSettings.newEvents !== false
    : true
  const isBroadcastsPending = isUserSettingsLoading || updateUserSettingsMutation.isPending

  const toggleBroadcasts = () => {
    if (isBroadcastsPending || !userSettings) return
    const nextValue = !isBroadcastsEnabled
    hapticFeedback.impactOccurred('light')
    updateUserSettingsMutation.mutate({
      newProducts: nextValue,
      newEvents: nextValue,
    })
  }

  const themeOptions: { value: Theme; label: string; icon: React.ReactNode }[] = [
    {
      value: 'platform',
      label: 'Авто',
      icon: <Smartphone className="w-4 h-4" />,
    },
    {
      value: 'light',
      label: 'Светлая',
      icon: <Sun className="w-4 h-4" />,
    },
    {
      value: 'dark',
      label: 'Тёмная',
      icon: <Moon className="w-4 h-4" />,
    },
  ]

  const currentIcon = effectiveTheme === 'dark'
    ? <Moon className="w-5 h-5 text-primary dark:text-primary-light" />
    : <Sun className="w-5 h-5 text-amber-500" />

  return (
    <div className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 delay-150">
      {/* Тема */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-border">
        <div className="flex items-center gap-3 mb-3">
          {currentIcon}
          <span className="text-sm font-medium text-ink dark:text-white">Тема</span>
          {isPlatform && (
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">Как в приложении</span>
          )}
        </div>
        <div className="flex gap-2">
          {themeOptions.map(({ value, label, icon }) => {
            const isActive = theme === value
            return (
              <button
                key={value}
                onClick={() => handleTheme(value)}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-xl text-xs font-medium transition-all',
                  isActive
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 dark:bg-dark-elevated text-gray-500 dark:text-gray-400 active:bg-gray-200 dark:active:bg-dark-border'
                )}
              >
                {icon}
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Уведомления о событиях */}
      {initData && (
        <button
          onClick={toggleNotifications}
          disabled={isPrefsLoading || updatePrefsMutation.isPending}
          className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-dark-border transition-colors hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated"
        >
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-ink dark:text-white">
              Напоминания о событиях
            </span>
          </div>
          {isPrefsLoading ? (
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
          ) : (
            <ToggleSwitch isActive={isNotificationsEnabled} />
          )}
        </button>
      )}

      {/* Рассылки */}
      {initData && (
        <button
          onClick={toggleBroadcasts}
          disabled={isBroadcastsPending || !userSettings}
          aria-pressed={isBroadcastsEnabled}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-dark-border transition-colors hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated disabled:opacity-60"
        >
          <div className="flex items-center gap-3 min-w-0 text-left">
            <Megaphone className="w-5 h-5 text-gray-400 shrink-0" />
            <div className="min-w-0">
              <span className="block text-sm font-medium text-ink dark:text-white">
                Рассылки
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {isBroadcastsEnabled ? 'Анонсы событий и новинки включены' : 'Вы отписаны. Можно включить снова'}
              </span>
            </div>
          </div>
          {isBroadcastsPending ? (
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
          ) : (
            <ToggleSwitch isActive={isBroadcastsEnabled} />
          )}
        </button>
      )}

      {/* Политика конфиденциальности */}
      <button
        onClick={handlePrivacyClick}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-dark-border transition-colors hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated"
      >
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-gray-400" />
          <span className="text-sm font-medium text-ink dark:text-white">
            Политика конфиденциальности
          </span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </button>

      {/* Публичная оферта */}
      <button
        onClick={handleOfferClick}
        className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-dark-elevated active:bg-gray-100 dark:active:bg-dark-elevated"
      >
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-gray-400" />
          <span className="text-sm font-medium text-ink dark:text-white">
            Публичная оферта
          </span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </button>
    </div>
  )
}

function ToggleSwitch({ isActive }: { isActive: boolean }) {
  return (
    <div className={cn(
      'w-10 h-6 rounded-full p-0.5 transition-colors duration-200',
      isActive ? 'bg-primary' : 'bg-gray-300'
    )}>
      <div className={cn(
        'w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200',
        isActive ? 'translate-x-4' : 'translate-x-0'
      )} />
    </div>
  )
}
